/**
 * Wiktionary lookup: returns rich definitions (multiple senses + examples).
 *
 * Per-locale strategy:
 *   en — REST `definition` endpoint on en.wiktionary.org (structured JSON).
 *   fr — `action=query&prop=extracts` on fr.wiktionary.org + plain-text parse.
 *   de — same on de.wiktionary.org with the `Bedeutungen:` block parser.
 *
 * The REST `definition` endpoint returns 501 on fr/de Wiktionaries, so the
 * action API is the only path to native-language definitions there.
 */
import { DEF_CACHE_KEY } from './storage';
import type { DefinitionEntry, Locale, Sense } from './types';

// In-memory mirror of the localStorage cache; loaded once on module init.
const defCache = new Map<string, DefinitionEntry | 'missing'>();
let cacheLoaded = false;

function loadCache(): void {
  if (cacheLoaded) return;
  cacheLoaded = true;
  try {
    const raw = localStorage.getItem(DEF_CACHE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw) as Record<string, DefinitionEntry | 'missing'>;
    for (const [k, v] of Object.entries(obj)) defCache.set(k, v);
  } catch {
    // ignore cache corruption
  }
}

function persistCache(): void {
  try {
    const obj: Record<string, DefinitionEntry | 'missing'> = {};
    for (const [k, v] of defCache) obj[k] = v;
    localStorage.setItem(DEF_CACHE_KEY, JSON.stringify(obj));
  } catch {
    // ignore quota errors
  }
}

const UPPER_ESZETT = 'ẞ';

function lookupForms(locale: Locale, word: string): string[] {
  const lower = word.toLowerCase().replace(new RegExp(UPPER_ESZETT, 'g'), 'ß');
  const title = lower.charAt(0).toUpperCase() + lower.slice(1);
  // German nouns are capitalized — title-case first so the `de` key shows up
  return locale === 'de' ? [title, lower] : [lower, title];
}

function stripHtml(s: string): string {
  const d = document.createElement('div');
  d.innerHTML = s;
  return d.textContent ?? '';
}

// ────────────────────────────────────────────────────────────────────────────
// EN — structured REST endpoint

async function fetchEnglishRich(word: string): Promise<DefinitionEntry | null> {
  for (const lookup of lookupForms('en', word)) {
    try {
      const url = `https://en.wiktionary.org/api/rest_v1/page/definition/${encodeURIComponent(lookup)}`;
      const res = await fetch(url);
      if (!res.ok) continue;
      const data = (await res.json()) as Record<string, unknown>;
      const arr =
        (Array.isArray(data?.en) && (data.en as unknown[])) ||
        (Array.isArray(data?.other) && (data.other as unknown[])) ||
        null;
      if (!arr || arr.length === 0) continue;
      const first = arr[0] as {
        partOfSpeech?: string;
        definitions?: Array<{
          definition?: string;
          parsedExamples?: Array<{ example?: string }>;
          examples?: string[];
        }>;
      };
      const senses: Sense[] = (first.definitions ?? [])
        .slice(0, 4)
        .map((d): Sense | null => {
          const gloss = stripHtml(d.definition ?? '').trim();
          if (!gloss) return null;
          const rawExamples = d.parsedExamples ?? d.examples ?? [];
          const examples = (rawExamples as unknown[])
            .slice(0, 2)
            .map((e) =>
              typeof e === 'string' ? stripHtml(e).trim() : stripHtml(((e as { example?: string }).example ?? '')).trim()
            )
            .filter((s): s is string => s.length > 0);
          return { gloss, examples };
        })
        .filter((s): s is Sense => s !== null);
      if (senses.length === 0) continue;
      return { word: lookup, partOfSpeech: first.partOfSpeech ?? '', senses };
    } catch {
      // try next form
    }
  }
  return null;
}

// ────────────────────────────────────────────────────────────────────────────
// FR — extract API + parser

function parseFrenchRich(extract: string, lookup: string): DefinitionEntry | null {
  const lines = extract.split('\n');
  let inFrench = false;
  let partOfSpeech = '';
  const senses: { gloss: string; examples: string[] }[] = [];
  let skippedHeader = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!.trim();

    if (/^==\s*Français\s*==$/.test(line)) { inFrench = true; continue; }
    if (inFrench && /^==[^=]/.test(line) && !/^==\s*Français/.test(line)) break;
    if (!inFrench) continue;

    const posMatch = line.match(/^===\s*(.+?)\s*===$/);
    if (posMatch) {
      const pos = posMatch[1]!;
      if (/^(Nom commun|Verbe|Adjectif|Adverbe|Pronom|Locution)/i.test(pos)) {
        partOfSpeech = pos.toLowerCase();
        skippedHeader = false;
      } else {
        partOfSpeech = '';
      }
      continue;
    }
    if (!partOfSpeech) continue;
    if (line.length === 0 || line.length < 18) continue;
    if (/^(Voir aussi|Forme de|Notes?|Variantes|Apparentés)/i.test(line)) continue;
    if (/\\.*?\\/.test(line)) { skippedHeader = true; continue; }
    if (!skippedHeader) continue;

    if (/^\(/.test(line) || senses.length === 0) {
      senses.push({ gloss: line, examples: [] });
    } else if (senses.length > 0 && senses[senses.length - 1]!.examples.length < 2) {
      senses[senses.length - 1]!.examples.push(line);
    }
    if (senses.length >= 4 && senses[senses.length - 1]!.examples.length >= 2) break;
  }

  if (senses.length === 0) return null;
  return { word: lookup, partOfSpeech, senses: senses.slice(0, 4) };
}

// ────────────────────────────────────────────────────────────────────────────
// DE — extract API + parser

function parseGermanRich(extract: string, lookup: string): DefinitionEntry | null {
  const posMatch = extract.match(
    /===\s*(Substantiv|Verb|Adjektiv|Adverb|Pronomen|Artikel|Konjunktion|Präposition|Numerale|Partikel)[^=]*===/
  );
  const partOfSpeech = posMatch ? posMatch[1]!.toLowerCase() : '';

  const bedMatch = extract.match(
    /Bedeutungen:\s*([\s\S]*?)(?=\n\s*(?:Beispiele:|Synonyme:|Herkunft:|Wortbildungen|Redewendungen|Gegenwörter:|Sinnverwandte|Charakteristische|Übersetzungen|===)|$)/
  );
  if (!bedMatch) return null;
  const bedBlock = bedMatch[1]!;

  const defRegex = /\[(\d+[a-z]?)\]\s*([^\n\[]+)/g;
  const sensesByNum: Record<string, { gloss: string; examples: string[] }> = {};
  const order: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = defRegex.exec(bedBlock)) !== null) {
    const num = m[1]!;
    const text = m[2]!.trim();
    if (!text) continue;
    if (!sensesByNum[num]) {
      sensesByNum[num] = { gloss: text, examples: [] };
      order.push(num);
    } else {
      sensesByNum[num]!.gloss = text;
    }
    if (order.length >= 4) break;
  }
  if (order.length === 0) return null;

  const beiMatch = extract.match(
    /Beispiele:\s*([\s\S]*?)(?=\n\s*(?:Charakteristische|Wortbildungen|Synonyme|Übersetzungen|Redewendungen|===)|$)/
  );
  if (beiMatch) {
    const beiBlock = beiMatch[1]!;
    const exRegex = /\[(\d+[a-z]?)\]\s*([^\n\[]+)/g;
    let em: RegExpExecArray | null;
    while ((em = exRegex.exec(beiBlock)) !== null) {
      const num = em[1]!;
      const text = em[2]!.trim();
      if (!text || !sensesByNum[num]) continue;
      if (sensesByNum[num]!.examples.length < 2) sensesByNum[num]!.examples.push(text);
    }
  }

  return { word: lookup, partOfSpeech, senses: order.map((n) => sensesByNum[n]!) };
}

async function fetchNativeRich(locale: 'fr' | 'de', word: string): Promise<DefinitionEntry | null> {
  const host = locale;
  const parser = locale === 'fr' ? parseFrenchRich : parseGermanRich;
  for (const lookup of lookupForms(locale, word)) {
    try {
      const url = `https://${host}.wiktionary.org/w/api.php?action=query&titles=${encodeURIComponent(lookup)}&prop=extracts&exchars=3500&explaintext=1&format=json&origin=*`;
      const res = await fetch(url);
      if (!res.ok) continue;
      const data = (await res.json()) as { query?: { pages?: Record<string, { extract?: string }> } };
      const pages = data?.query?.pages;
      if (!pages) continue;
      for (const id in pages) {
        if (id === '-1') continue;
        const ext = pages[id]?.extract;
        if (!ext) continue;
        const parsed = parser(ext, lookup);
        if (parsed) return parsed;
      }
    } catch {
      // try next form
    }
  }
  return null;
}

// ────────────────────────────────────────────────────────────────────────────
// Public entry — single cached lookup per (locale, word)

export async function fetchDefinition(
  locale: Locale,
  word: string
): Promise<DefinitionEntry | null> {
  loadCache();
  const cacheKey = `${locale}|${word.toUpperCase()}`;
  const cached = defCache.get(cacheKey);
  if (cached !== undefined) return cached === 'missing' ? null : cached;

  let result: DefinitionEntry | null = null;
  if (locale === 'fr' || locale === 'de') {
    result = await fetchNativeRich(locale, word);
    if (!result) result = await fetchEnglishRich(word);  // graceful fallback
  } else {
    result = await fetchEnglishRich(word);
  }

  if (!result) {
    defCache.set(cacheKey, 'missing');
    persistCache();
    return null;
  }

  defCache.set(cacheKey, result);
  persistCache();
  return result;
}
